/**
 * AchievementScene.ts — Achievement browser with category menu and pagination.
 *
 * Two-screen flow:
 * 1. Category selection — menu listing all categories with unlock counts
 * 2. Achievement list — paginated display within the selected category
 *
 * Migrated to FrameRenderer + PanelWriter for framed UI layout.
 */

import type { GameEngine, Scene } from '@core/engine/GameEngine';
import type { MenuOption } from '@ui/MenuSystem';
import type { ColorName } from '@ui/ColorTheme';
import type { Achievement, AchievementCategory } from '@game/data/types';
import type { GallowcrestVault } from '@core/engine/SaveLoad';
import type { AchievementStatus } from '@core/systems/AchievementSystem';
import type { HintTriggerType } from '@ui/HintManager';
import type { PanelWriter } from '@ui/PanelWriter';
import { achievements as allAchievements } from '@game/data/achievements';

const CATEGORIES: { id: AchievementCategory | 'all'; label: string }[] = [
  { id: 'all', label: 'All' },
  { id: 'combat', label: 'Combat' },
  { id: 'exploration', label: 'Exploration' },
  { id: 'social', label: 'Social' },
  { id: 'choices', label: 'Choices' },
  { id: 'class_mastery', label: 'Class Mastery' },
  { id: 'challenge', label: 'Challenge' },
  { id: 'hidden', label: 'Hidden' },
];

const RARITY_COLORS: Record<string, ColorName> = {
  common: 'white',
  uncommon: 'green',
  rare: 'frostBlue',
  epic: 'magenta',
  legendary: 'yellow',
};

const ITEMS_PER_PAGE = 5;

export class AchievementScene implements Scene {
  name = 'AchievementScene';

  async enter(engine: GameEngine): Promise<void> {
    const ctx = engine.getContext();

    // Clear terminal to prevent title screen bleed-through when pushed as overlay
    ctx.terminal.clear();
    ctx.sprites.hide();
    ctx.visualLayer.setLocationParticles('none');

    // Trigger achievement hint
    await ctx.hintManager.maybeShowHint('first_achievement' as HintTriggerType);

    while (true) {
      const action = await this.showCategoryMenu(engine);
      if (action === 'back') {
        await engine.popScene();
        return;
      }
      // User selected a category — browse it
      await this.browseCategory(engine, action);
    }
  }

  async exit(engine: GameEngine): Promise<void> {
    const ctx = engine.getContext();
    ctx.sprites.hide();
    ctx.frameRenderer.clearFooter();
  }

  // ─── Category Selection ────────────────────────────────

  private async showCategoryMenu(
    engine: GameEngine,
  ): Promise<'back' | AchievementCategory | 'all'> {
    const ctx = engine.getContext();
    const { menu, achievementSystem, saveSystem, frameRenderer, effects } = ctx;
    const vault = saveSystem.loadVault();

    // Summary info
    const unlockedCount = achievementSystem.getUnlockedCount(vault);
    const totalCount = achievementSystem.getTotalCount();
    const unlockedPoints = achievementSystem.getUnlockedPoints(vault);
    const totalPoints = achievementSystem.getTotalPoints();
    const footer = `${unlockedCount}/${totalCount} Unlocked  |  ${unlockedPoints}/${totalPoints} Points`;

    ctx.sprites.hide();
    const layout = frameRenderer.drawFrame({
      title: 'ACHIEVEMENTS',
      headerRight: `${unlockedCount}/${totalCount}`,
      footer,
      borderColor: 'frostBlue',
    });

    const writer = frameRenderer.createWriter(layout.content, effects);
    writer.blankLine();

    // Category menu items with counts
    const options: MenuOption[] = CATEGORIES.map(cat => {
      const catAchievements = cat.id === 'all'
        ? allAchievements
        : allAchievements.filter(a => a.category === cat.id);
      const count = catAchievements.length;
      const unlocked = catAchievements.filter(a =>
        vault.unlockedAchievements.includes(a.id),
      ).length;

      return {
        label: cat.label,
        color: (unlocked === count && count > 0 ? 'green' : 'white') as ColorName,
        suffix: `(${unlocked}/${count})`,
        suffixColor: (unlocked === count && count > 0 ? 'green' : 'frostSilver') as ColorName,
      };
    });

    const menuCfg = writer.menuConfig();
    const sel = await menu.show(options, {
      showExit: true,
      exitLabel: 'Back',
      startRow: menuCfg.startRow,
      startCol: menuCfg.startCol,
      maxWidth: menuCfg.maxWidth,
      onBeforeRender: () => {
        ctx.sprites.hide();
        frameRenderer.drawFrame({
          title: 'ACHIEVEMENTS',
          headerRight: `${unlockedCount}/${totalCount}`,
          footer,
          borderColor: 'frostBlue',
        });
      },
    });

    if (sel === -1) return 'back';
    return CATEGORIES[sel].id;
  }

  // ─── Category Browser (Paginated) ─────────────────────

  private async browseCategory(
    engine: GameEngine,
    categoryId: AchievementCategory | 'all',
  ): Promise<void> {
    const ctx = engine.getContext();
    const { menu, effects, achievementSystem, saveSystem, gameState, frameRenderer } = ctx;
    const vault = saveSystem.loadVault();
    const categoryFilter = categoryId === 'all' ? undefined : categoryId;
    const catLabel = CATEGORIES.find(c => c.id === categoryId)?.label ?? 'All';

    const statuses = gameState
      ? achievementSystem.getStatuses(gameState, vault, categoryFilter)
      : this.getVaultOnlyStatuses(vault, categoryFilter);

    const totalPages = Math.max(1, Math.ceil(statuses.length / ITEMS_PER_PAGE));
    let page = 0;

    while (true) {
      ctx.sprites.hide();
      const footer = `Page ${page + 1}/${totalPages}  |  ${catLabel}`;

      const layout = frameRenderer.drawFrame({
        title: `ACHIEVEMENTS \u2014 ${catLabel}`,
        headerRight: `${page + 1}/${totalPages}`,
        footer,
        borderColor: 'frostBlue',
      });

      const writer = frameRenderer.createWriter(layout.content, effects);
      writer.blankLine();

      // Render achievements for this page
      const start = page * ITEMS_PER_PAGE;
      const end = Math.min(start + ITEMS_PER_PAGE, statuses.length);
      const pageStatuses = statuses.slice(start, end);

      if (pageStatuses.length === 0) {
        writer.writeLine('  No achievements in this category.', 'khaki');
        writer.blankLine();
      } else {
        for (let i = 0; i < pageStatuses.length; i++) {
          this.renderAchievement(writer, pageStatuses[i], engine);
          if (i < pageStatuses.length - 1) {
            writer.blankLine();
          }
        }
      }

      writer.blankLine();

      // Build navigation menu
      const options: MenuOption[] = [];
      if (totalPages > 1) {
        options.push({
          label: 'Next Page \u2192',
          color: 'white',
          disabled: page >= totalPages - 1,
        });
        options.push({
          label: '\u2190 Previous Page',
          color: 'white',
          disabled: page <= 0,
        });
      }

      const menuCfg = writer.menuConfig();
      const sel = await menu.show(options, {
        showExit: true,
        exitLabel: 'Back to Categories',
        startRow: menuCfg.startRow,
        startCol: menuCfg.startCol,
        maxWidth: menuCfg.maxWidth,
        onBeforeRender: () => {
          ctx.sprites.hide();
          frameRenderer.drawFrame({
            title: `ACHIEVEMENTS \u2014 ${catLabel}`,
            headerRight: `${page + 1}/${totalPages}`,
            footer,
            borderColor: 'frostBlue',
          });
        },
      });

      if (sel === -1) return; // Back to categories

      // Handle pagination
      if (totalPages > 1) {
        if (sel === 0) {
          page = Math.min(page + 1, totalPages - 1);
        } else if (sel === 1) {
          page = Math.max(page - 1, 0);
        }
      }
    }
  }

  // ─── Achievement Renderer ─────────────────────────────

  private renderAchievement(writer: PanelWriter, status: AchievementStatus, engine: GameEngine): void {
    const { achievement, unlocked, progressText } = status;
    const ctx = engine.getContext();

    const isHidden = achievement.isHidden && !unlocked;

    const icon = unlocked ? '\u2713' : '\u25CB';
    const iconColor: ColorName = unlocked ? 'green' : 'charcoal';
    const rarityColor = RARITY_COLORS[achievement.rarity] ?? 'white';

    if (isHidden) {
      writer.writeLine(`  ${icon} ???  ${achievement.hintText ? `(${achievement.hintText})` : '(Hidden Achievement)'}`, iconColor);
    } else {
      // Name line: icon + name + points
      writer.beginLine();
      writer.write(`  ${icon} ${achievement.name}`, unlocked ? rarityColor : 'white');

      // Tier badge with color (Bronze/Silver/Gold)
      if (achievement.tier && achievement.tier !== 'none') {
        const tierColor: ColorName = achievement.tier === 'gold' ? 'runeGold'
          : achievement.tier === 'silver' ? 'frostSilver'
          : 'amber'; // bronze
        writer.write(` [${achievement.tier.charAt(0).toUpperCase() + achievement.tier.slice(1)}]`, tierColor);
      }

      writer.write(`  ${achievement.points} pts`, unlocked ? rarityColor : 'khaki');
      writer.endLine();

      // Description
      writer.writeLine(`      ${achievement.description}`, 'white');

      // Progress bar for counter-based achievements (using ProgressIndicator)
      if (!unlocked && progressText) {
        // Extract current/target from progressText (format: "X / Y")
        const match = progressText.match(/(\d+)\s*\/\s*(\d+)/);
        if (match) {
          const current = parseInt(match[1], 10);
          const target = parseInt(match[2], 10);
          const progressBar = ctx.progressIndicator.renderBar(current, target);
          writer.writeLine(`      ${progressBar} ${progressText}`, 'frostSilver');
        } else {
          // Fallback for non-standard progress text
          writer.writeLine(`      ${progressText}`, 'frostSilver');
        }
      }
    }
  }

  // ─── Vault-Only Statuses ──────────────────────────────

  private getVaultOnlyStatuses(
    vault: GallowcrestVault,
    category?: AchievementCategory,
  ): AchievementStatus[] {
    let list: Achievement[] = allAchievements;
    if (category) {
      list = list.filter(a => a.category === category);
    }

    return list.map(achievement => ({
      achievement,
      unlocked: vault.unlockedAchievements.includes(achievement.id),
      progress: vault.unlockedAchievements.includes(achievement.id) ? 1 : 0,
    }));
  }
}
